
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const TermsOfServiceEn = () => {
  const { t } = useTranslation(['common']);

  return (
    <div className="min-h-screen bg-uplivion-lightBlue/30 py-8">
      <div className="container mx-auto px-6 max-w-4xl">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="mb-8">
            <Link to="/" className="text-uplivion-blue hover:underline mb-4 inline-block">
              ← Back to Home
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Terms of Service</h1>
            <p className="text-gray-600">Effective from: June 28, 2025 | Version 1.1</p>
          </div>

          <div className="prose max-w-none">
            <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-8">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-orange-700">
                    <strong>Important Notice:</strong> uplivion is currently in beta testing phase. 
                    The service may have limitations and is not yet fully mature. 
                    By using it, you agree to help improve the service.
                  </p>
                </div>
              </div>
            </div>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">1. Scope and Contracting Party</h2>
              <p className="mb-4">
                These Terms of Service govern the use of the uplivion platform 
                (accessible at www.uplivion.life and app.uplivion.life), operated by Arc Rider Ventures GmbH.
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <h3 className="font-semibold mb-2">Provider and Contracting Party:</h3>
                <p className="mb-2">
                  <strong>Arc Rider Ventures GmbH</strong><br/>
                  Jänickendorfer Str. 64a<br/>
                  14943 Brandenburg<br/>
                  Germany
                </p>
                <p className="mb-2">
                  <strong>Commercial Register:</strong> Local Court Berlin Charlottenburg HRB199081B<br/>
                  <strong>Managing Director:</strong> Jakob Jordan
                </p>
                <p>
                  <strong>Contact:</strong> support@uplivion.life
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">2. Service Description</h2>
              <p className="mb-4">
                uplivion is a digital platform for creating and managing personal life timelines. 
                Users can:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Organize personal memories and events chronologically</li>
                <li>Add photos and texts to life events</li>
                <li>Share timelines with friends (with privacy settings)</li>
                <li>Document their life story in a structured way</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">3. Technical Infrastructure</h2>
              <p className="mb-4">
                uplivion exclusively uses European service providers for optimal performance, 
                data protection, and legal compliance:
              </p>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-green-800 mb-2">🇪🇺 100% European Infrastructure</h4>
                <p className="text-sm text-green-700">
                  All services are provided by EU-based providers and are subject to GDPR.
                </p>
              </div>

              <div className="grid md:grid-cols-1 gap-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Website Hosting & CDN</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Provider:</strong> Netlify (European CDN Edge Locations)
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Optimized performance for European users</li>
                    <li>• Server locations: Frankfurt, London</li>
                    <li>• 99.9% availability guarantee</li>
                    <li>• Automatic SSL encryption</li>
                  </ul>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Database & Backend</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Provider:</strong> Supabase (Germany)
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Database hosting in Frankfurt</li>
                    <li>• Real-time synchronization</li>
                    <li>• Automatic backups</li>
                    <li>• End-to-end encryption</li>
                  </ul>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Email Services</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Provider:</strong> Resend (Ireland)
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Transactional emails</li>
                    <li>• GDPR-compliant delivery</li>
                    <li>• High delivery rates</li>
                    <li>• Encrypted transmission</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">4. Beta Phase and Availability</h2>
              <p className="mb-4">
                uplivion is in beta testing phase. This means:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>The service may be temporarily unavailable</li>
                <li>Features may change without prior notice</li>
                <li>Data loss may occur in rare cases</li>
                <li>Bugs and performance issues are possible</li>
                <li>Regular updates and improvements are performed</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">4.1 Service Dependencies</h3>
              <p className="mb-4">
                The availability of uplivion depends on our European infrastructure partners:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Website availability:</strong> Dependent on Netlify CDN (EU)</li>
                <li><strong>Database services:</strong> Dependent on Supabase (Germany)</li>
                <li><strong>Email functions:</strong> Dependent on Resend (Ireland)</li>
              </ul>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">Maintenance Windows</h4>
                <p className="text-sm text-blue-700">
                  Planned maintenance work will be announced at least 24 hours in advance by email. 
                  Unplanned outages will be resolved and communicated as quickly as possible.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">5. User Account and Registration</h2>
              <p className="mb-4">
                Registration is required to use uplivion. During registration, you must:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Provide true and complete information</li>
                <li>Choose a secure password</li>
                <li>Keep your contact details up to date</li>
                <li>Take responsibility for the security of your account</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">6. Usage Guidelines</h2>
              <p className="mb-4">When using uplivion, you agree to:</p>
              <ul className="list-disc pl-6 mb-4">
                <li>Not publish illegal, harmful, or offensive content</li>
                <li>Respect copyrights and personal rights of third parties</li>
                <li>Not distribute spam or advertising content</li>
                <li>Not impair the technical infrastructure</li>
                <li>Not use automated tools without permission</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">7. Subscription Plans and Pricing</h2>
              <p className="mb-4">
                uplivion offers various usage models:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Free Plan:</strong> Basic functions with storage limitations</li>
                <li><strong>Premium Plans:</strong> Extended features and more storage (planned)</li>
              </ul>
              <p className="mb-4">
                Prices and services may change during the beta phase. 
                Existing users will be informed of changes in advance.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">8. Data Protection and Content</h2>
              <p className="mb-4">
                The protection of your data is important to us. Details on data protection can be found in our 
                <Link to="/privacy-en" className="text-uplivion-blue hover:underline"> Privacy Policy</Link>.
              </p>
              <p className="mb-4">
                You retain the rights to your content. However, by uploading, you grant us 
                the technically necessary usage rights to provide the service.
              </p>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-800 mb-2">Privacy Guarantees</h4>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>• All data remains in the EU (Germany, Ireland)</li>
                  <li>• No sharing with third parties without your consent</li>
                  <li>• Full GDPR compliance of all partners</li>
                  <li>• Deletion or export of your data possible at any time</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">9. Limitation of Liability</h2>
              
              <h3 className="text-xl font-semibold mb-3">9.1 General Liability Limitations</h3>
              <p className="mb-4">
                Due to the beta phase, we assume only limited liability:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>No warranty for uninterrupted availability</li>
                <li>No liability for data loss (to the extent legally permissible)</li>
                <li>Limited liability for indirect damages</li>
                <li>Liability for intent and gross negligence remains unaffected</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">9.2 Liability for Third-Party Services</h3>
              <p className="mb-4">
                For failures or problems of our infrastructure partners, we are only liable to a limited extent:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Netlify outages:</strong> No liability for CDN-related unavailability</li>
                <li><strong>Supabase problems:</strong> Limited liability for database outages</li>
                <li><strong>Resend disruptions:</strong> No liability for email delivery problems</li>
              </ul>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-800 mb-2">Service Level Agreements (SLA)</h4>
                <p className="text-sm text-yellow-700">
                  During the beta phase, no fixed SLAs apply yet. We strive for 
                  maximum availability and transparent communication during problems.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">10. Termination</h2>
              <p className="mb-4">
                You can cancel your account at any time. We reserve the right to suspend or delete accounts 
                for violations of these terms of service.
              </p>
              
              <h3 className="text-xl font-semibold mb-3">10.1 Data Export Upon Termination</h3>
              <p className="mb-4">
                Upon account deletion, you have the right to:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Complete export of your timeline data</li>
                <li>Download of all uploaded images</li>
                <li>Copy of your profile information</li>
                <li>30 days grace period before final deletion</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">11. Changes to Terms of Service</h2>
              <p className="mb-4">
                We may change these terms of service at any time. You will be informed about significant changes 
                by email or via the platform.
              </p>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">Amendment Procedure</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• At least 14 days advance notice for significant changes</li>
                  <li>• Email notification to all registered users</li>
                  <li>• Right to object with possibility of termination</li>
                  <li>• Clear presentation of changes</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">12. Applicable Law</h2>
              <p className="mb-4">
                German law applies. Place of jurisdiction is Brandenburg an der Havel, as far as legally permissible.
              </p>
              
              <h3 className="text-xl font-semibold mb-3">12.1 EU Law</h3>
              <p className="mb-4">
                Additionally applicable:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>EU General Data Protection Regulation (GDPR)</li>
                <li>EU Consumer Rights Directive</li>
                <li>EU Directive on Digital Content</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">13. Contact</h2>
              <p className="mb-4">
                For questions about these terms of service, contact:
              </p>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold mb-2">Business Inquiries</h4>
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Arc Rider Ventures GmbH</strong><br/>
                      Jänickendorfer Str. 64a<br/>
                      14943 Brandenburg<br/>
                      Germany
                    </p>
                    <p className="text-sm text-gray-600">
                      <strong>Managing Director:</strong> Jakob Jordan<br/>
                      <strong>Commercial Register:</strong> HRB199081B (Local Court Berlin Charlottenburg)
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold mb-2">Support Contact</h4>
                    <p className="text-sm text-gray-600">
                      <strong>All inquiries:</strong> support@uplivion.life
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <div className="mt-8 pt-8 border-t">
            <Button asChild>
              <Link to="/">Back to Home</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServiceEn;
